import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { getPermissions, Permission } from '../../services/api/team.service';
import { toast } from 'react-toastify';
import { Shield, Search, ChevronDown, ChevronUp } from 'lucide-react';

const PermissionsList: React.FC = () => {
  const [permissions, setPermissions] = useState<Record<string, Permission[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Get role_id from sessionStorage and check if user has access
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole');
    } catch {
      return null;
    }
  };

  const systemRole = getSystemRole();

  // Redirect to dashboard if not system admin
  if (systemRole !== "system_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPermissions();
      setPermissions(data);
      // Expand all categories by default
      setExpandedCategories(new Set(Object.keys(data)));
    } catch (err: any) {
      console.error('Error fetching permissions:', err);
      setError(err.message || 'Failed to fetch permissions');
      toast.error(err.message || 'Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const filterPermissions = (perms: Permission[]): Permission[] => {
    if (!searchQuery.trim()) return perms;
    const query = searchQuery.toLowerCase();
    return perms.filter(
      (perm) =>
        perm.permissionCode.toLowerCase().includes(query) ||
        perm.permissionName.toLowerCase().includes(query) ||
        (perm.description && perm.description.toLowerCase().includes(query))
    );
  };

  const filteredCategories = Object.entries(permissions).filter(([category, perms]) => {
    if (!searchQuery.trim()) return true;
    const filtered = filterPermissions(perms);
    return filtered.length > 0 || category.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Permissions
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Permissions
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  const totalPermissions = Object.values(permissions).reduce((sum, perms) => sum + perms.length, 0);

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div className="flex justify-between items-center mt-[3rem] mb-6">
          <div>
            <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">
              Permissions
            </span>
            <p className="text-sm text-gray-600 font-family-messina-sans mt-1">
              {totalPermissions} permissions across {Object.keys(permissions).length} categories
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 font-family-messina-sans"
              placeholder="Search permissions by name, code, or description..."
            />
          </div>
        </div>

        {/* Permissions by Category */}
        <div className="space-y-4">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="text-gray-600 font-family-messina-sans">
                No permissions found matching "{searchQuery}"
              </div>
            </div>
          ) : (
            filteredCategories.map(([category, categoryPerms]) => {
              const filteredPerms = filterPermissions(categoryPerms);
              if (filteredPerms.length === 0) return null;

              return (
                <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern capitalize">
                        {category}
                      </h3>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-family-messina-sans">
                        {filteredPerms.length} {filteredPerms.length === 1 ? 'permission' : 'permissions'}
                      </span>
                    </div>
                    {expandedCategories.has(category) ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </button>

                  {expandedCategories.has(category) && (
                    <div className="border-t border-gray-200">
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredPerms.map((permission) => (
                            <div
                              key={permission.permissionId}
                              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <h4 className="text-sm font-semibold text-gray-900 font-family-messina-sans mb-1">
                                    {permission.permissionName}
                                  </h4>
                                  <code className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded font-family-messina-sans">
                                    {permission.permissionCode}
                                  </code>
                                </div>
                                {permission.isActive !== false ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-family-messina-sans">
                                    Active
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-family-messina-sans">
                                    Inactive
                                  </span>
                                )}
                              </div>
                              {permission.description && (
                                <p className="text-xs text-gray-600 font-family-messina-sans mt-2">
                                  {permission.description}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsList;

